// /src/components/Upperbar.tsx

// What? A component for the upper bar of the application.
// Why? To display the upper bar including the logo and main user settings items. Also manages the theme (light/dark mode).
// How?
// - Manages dark mode state using useState, allowing for dynamic theme switching
// - Initializes theme based on system preference or stored preference using useEffect
// - Enables navigation to home page when logo is clicked using useNavigate
// - Provides a Switch component for users to manually toggle between dark and light themes
// - Displays the application logo using an imported image path
// - Implements local storage to persist user's theme preference across sessions
// - Dynamically applies the chosen theme to the document root for consistent styling
// This implementation creates a flexible and user-friendly upper bar that adapts to user preferences
// and provides easy navigation, fulfilling the component's purpose of managing the application's
// main settings and branding in the upper bar.

import React from "react";
import { Link } from "react-router-dom";
import { Box, IconButton, Tooltip } from "@mui/joy";
import MenuIcon from "@mui/icons-material/Menu";
import ThemeLogo from "./ThemeLogo";
import UpperbarMenu from "./UpperBarMenu";

interface UpperbarProps {
  onToggleSidebar?: () => void;
}

export const UPPERBAR_HEIGHT_PX = 56;

const Upperbar: React.FC<UpperbarProps> = ({ onToggleSidebar }) => {
  return (
    <Box
      component="header"
      data-testid="app-upperbar"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        minHeight: `${UPPERBAR_HEIGHT_PX}px`,
        zIndex: 1100,
        backgroundColor: "background.surface",
        borderBottom: "1px solid",
        borderColor: "divider",
        display: "flex",
        alignItems: "center",
        boxSizing: "border-box",
        px: 0,
        py: 1,
      }}
    >
      {/* Logo - Hidden on smaller screens */}
      <Box
        sx={{
          display: { xs: "none", sm: "flex" },
          alignItems: "center",
          justifyContent: "flex-start", // Left-align the logo container
          flexShrink: 0,
          pl: 1, // Add left padding to logo container
          mr: 1,
          width: "fit-content", // Make container fit its content
        }}
      >
        <Link to="/">
          <ThemeLogo />
        </Link>
      </Box>

      {/* Sidebar Toggle Button - Always visible, positioned after logo */}
      {onToggleSidebar && (
        <Tooltip title="Toggle sidebar">
          <IconButton
            onClick={onToggleSidebar}
            variant="plain"
            color="neutral"
            sx={{
              flexShrink: 0,
            }}
          >
            <MenuIcon />
          </IconButton>
        </Tooltip>
      )}

      {/* Menu - Always visible, takes remaining space */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          height: "100%", // Fixed height for menu alignment
        }}
      >
        <UpperbarMenu />
      </Box>
    </Box>
  );
};

export default Upperbar;
